import { Button, Input, Switch, Table, Tooltip } from "antd";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import deleteWhiteIcon from "../../../assets/images/icon/deleteWhiteIcon.png";
import EditIcon from "../../../assets/images/edit.svg";
import Plus from "../../../assets/images/plus.svg";
import DeleteModal from "../../../components/DeleteModal";
import SectionWrapper from "../../../components/SectionWrapper";
import ShowTotal from "../../../components/ShowTotal";
import { AppStateContext } from "../../../context/AppContext";
import apiPath from "../../../constants/apiPath";
import lang from "../../../helper/langHelper";
import { Severty, ShowToast } from "../../../helper/toast";
import useDebounce from "../../../hooks/useDebounce";
import useRequest from "../../../hooks/useRequest";
import AddForm from "./AddForm";

const formatSize = (bytes) => {
  const size = Number(bytes || 0);
  if (!size) return "-";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

function Index() {
  const heading = "Digital Documents";
  const { setPageHeading } = useContext(AppStateContext);
  const { request } = useRequest();
  const requestRef = useRef(request);
  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState();
  const [deleteModal, showDeleteModal] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const debouncedSearchText = useDebounce(searchText, 300);
  const api = { addEdit: apiPath.digitalDocuments, list: apiPath.digitalDocuments };

  useEffect(() => {
    requestRef.current = request;
  }, [request]);

  const fetchData = useCallback(
    (pageConfig = { current: 1, pageSize: 10 }) => {
      setLoading(true);
      requestRef.current({
        url: `${api.list}?page=${pageConfig?.current || 1}&pageSize=${pageConfig?.pageSize || 10}&search=${debouncedSearchText}`,
        method: "GET",
        onSuccess: ({ data }) => {
          setLoading(false);
          setList((data?.docs || []).map((item) => ({ ...item, key: item._id })));
          setPagination((prev) => ({ ...prev, current: pageConfig?.current || 1, pageSize: pageConfig?.pageSize || 10, total: data?.totalDocs || 0 }));
        },
        onError: (error) => {
          setLoading(false);
          ShowToast(error?.response?.data?.message || error?.message || "Failed to load digital documents", Severty.ERROR);
        },
      });
    },
    [api.list, debouncedSearchText],
  );

  useEffect(() => {
    setPageHeading(heading);
  }, [setPageHeading]);

  useEffect(() => {
    fetchData({ current: 1, pageSize: pagination.pageSize });
  }, [debouncedSearchText, fetchData, pagination.pageSize, refresh]);

  const onDelete = (id) => {
    requestRef.current({
      url: `${api.addEdit}/${id}`,
      method: "DELETE",
      onSuccess: (response) => {
        ShowToast(response.message, Severty.SUCCESS);
        showDeleteModal(false);
        setSelected(undefined);
        setRefresh((prev) => !prev);
      },
      onError: (error) => ShowToast(error?.response?.data?.message || error?.message || "Failed to delete document", Severty.ERROR),
    });
  };

  const columns = [
    { title: "Title", dataIndex: "title", key: "title", render: (_, record) => <span className="cap">{record.title || "-"}</span> },
    { title: "File", dataIndex: "file_name", key: "file_name", render: (_, record) => <span>{record.file_name || "-"}</span> },
    { title: "Size", dataIndex: "file_size", key: "file_size", render: (_, record) => <span>{formatSize(record.file_size)}</span> },
    { title: "Type", dataIndex: "file_type", key: "file_type", render: () => <span>PDF</span> },
    { title: "Downloads", dataIndex: "download_count", key: "download_count", render: (_, record) => <span>{record.download_count || 0}</span> },
    { title: "Status", dataIndex: "status", key: "status", render: (_, record) => <Switch checked={record.status === "active"} disabled /> },
    {
      title: lang("Action"),
      key: "action",
      render: (_, record) => (
        <div className="d-flex justify-contenbt-start">
          <Tooltip title={lang("Edit")} color={"purple"}>
            <Button className="edit-cls btnStyle primary_btn" onClick={() => { setSelected(record); setVisible(true); }}>
              <img src={EditIcon} alt="" />
            </Button>
          </Tooltip>
          <Tooltip title={lang("Delete")} color={"purple"}>
            <Button className="btnStyle deleteDangerbtn" onClick={() => { setSelected(record); showDeleteModal(true); }}>
              <img src={deleteWhiteIcon} alt="" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <SectionWrapper
        cardHeading={heading}
        extra={
          <div className="w-100 text-head_right_cont flex-wrap gap-2">
            <div className="pageHeadingSearch">
              <Input.Search className="searchInput" placeholder="Search documents" onChange={(e) => setSearchText(e.target.value)} allowClear />
            </div>
            <Button className="primary_btn btnStyle" onClick={() => { setSelected(undefined); setVisible(true); }}>
              <span className="add-Ic"><img src={Plus} alt="" /></span>
              Add Document
            </Button>
          </div>
        }
      >
        <h4 className="text-right mb-1cont-space cont-space">{ShowTotal(pagination.total || 0)}</h4>
        <div className="table-responsive customPagination checkBoxSrNo">
          <Table
            loading={loading}
            columns={columns}
            dataSource={list}
            pagination={{ current: pagination.current, total: pagination.total, pageSize: pagination.pageSize, showSizeChanger: true, showQuickJumper: true, pageSizeOptions: ["10", "20", "30", "50"] }}
            onChange={(nextPagination) => fetchData(nextPagination)}
            className="ant-border-space keyword-emotion-table"
          />
        </div>
      </SectionWrapper>
      {visible && <AddForm api={api} show={visible} hide={() => { setSelected(undefined); setVisible(false); }} data={selected} refresh={() => setRefresh((prev) => !prev)} />}
      {deleteModal && (
        <DeleteModal
          title="Delete Digital Document"
          subtitle="This action will remove the document record. Existing product links will stop using it. Are you sure?"
          show={deleteModal}
          hide={() => { showDeleteModal(false); setSelected(undefined); }}
          onOk={() => onDelete(selected?._id)}
        />
      )}
    </>
  );
}

export default Index;
